/**
 * Classification helpers for document-conversion errors.
 *
 * A file above the in-browser preview size cap is refused deliberately and
 * permanently by the content service. The viewer needs to tell that apart from
 * a genuine failure so it can present an explanation and a download path
 * instead of a red "unexpected error", and so it does not offer a Retry button
 * for something retrying can never fix.
 */

/** Marker phrase the content service puts in its refusal message. Matching on
 *  it keeps the legacy path working when the structured code was dropped. */
const TOO_LARGE_PHRASE = 'limit for in-browser preview';

const GRPC_PREFIX = /^\s*\d{1,2}\s+[A-Z_]+:\s*/;

function readCandidateStrings(error: unknown): { codes: string[]; messages: string[] } {
  const codes: string[] = [];
  const messages: string[] = [];

  const push = (code: unknown, message: unknown) => {
    if (typeof code === 'string' && code) codes.push(code);
    if (typeof message === 'string' && message) messages.push(message);
  };

  if (typeof error === 'string') {
    messages.push(error);
  } else if (error && typeof error === 'object') {
    const e = error as Record<string, any>;
    push(e['code'], e['message']);
    const nested = e['response']?.data?.error ?? e['response']?.data ?? e['error'];
    if (nested && typeof nested === 'object') {
      push(nested['code'], nested['message']);
    }
  }

  return { codes, messages };
}

/**
 * Whether this error is the size-cap refusal.
 *
 * Deliberately narrow: a bare FAILED_PRECONDITION is NOT enough, because the
 * conversion route raises other preconditions (e.g. "Content file not
 * accessible"). Either the structured code says FILE_TOO_LARGE, or the message
 * carries the service's own preview-limit phrasing.
 */
export function isFileTooLargeError(error: unknown): boolean {
  const { codes, messages } = readCandidateStrings(error);
  if (codes.includes('FILE_TOO_LARGE')) return true;
  return messages.some((m) => m.includes(TOO_LARGE_PHRASE));
}

/**
 * The server's human-readable message, with any gRPC status prefix
 * ("9 FAILED_PRECONDITION: ") removed. Returns undefined when nothing usable
 * is present, so callers can fall back to their own wording.
 */
export function extractServerMessage(error: unknown): string | undefined {
  const { messages } = readCandidateStrings(error);
  for (const raw of messages) {
    const cleaned = raw.replace(GRPC_PREFIX, '').trim();
    if (cleaned) return cleaned;
  }
  return undefined;
}
